import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { UAParser } from 'ua-parser-js';
import {
  Brain,
  User,
  ChevronDown,
  ChevronUp,
  Wand2,
  MonitorSmartphone,
  Smartphone,
  Info
} from 'lucide-react';
import { BackgroundAnalysisStatus } from '../types';
import { formatTime } from '../services/backgroundAnalysisService';
import { getRandomExample } from '../utils/exampleQueries';
import { saveSuggestedQuestions } from '../services/abTestService';
import { validateAndSendExpertStat } from '../services/expertValidationService';
import { useLists } from '../contexts/ListsContext';
import { cn } from '../utils/cn';
import { useTranslation } from 'react-i18next';

interface QuickAnalysisFormProps {
  className?: string;
  onStartAnalysis: (description?: string, expert?: string, person?: string, question?: string, noQuestion?: boolean) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  backgroundAnalysisStatus?: BackgroundAnalysisStatus | null;
  error?: string | null;
  onClearError?: () => void;
}

const detectIsMobileDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const parser = new UAParser(navigator.userAgent);
  const deviceType = parser.getDevice().type;
  return deviceType === 'mobile' || deviceType === 'tablet';
};

const MAX_DESCRIPTION_LENGTH = 300;
const MIN_DESCRIPTION_LENGTH = 15;

const QuickAnalysisForm: React.FC<QuickAnalysisFormProps> = ({
  className,
  onStartAnalysis,
  onCancel,
  isLoading = false,
  backgroundAnalysisStatus,
  error,
  onClearError
}) => {
  const { t, i18n } = useTranslation('landing');
  const { experts, persons, loading: listsLoading } = useLists();

  const [description, setDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [highlightField, setHighlightField] = useState(false);
  const [showExpertSection, setShowExpertSection] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState('');
  const [showCustomExpert, setShowCustomExpert] = useState(false);
  const [customExpert, setCustomExpert] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [showCustomPerson, setShowCustomPerson] = useState(false);
  const [customPerson, setCustomPerson] = useState('');
  const [lastSubmittedDescription, setLastSubmittedDescription] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(() => detectIsMobileDevice());
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState(false);
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const [showMaritalStatusDropdown, setShowMaritalStatusDropdown] = useState(false);
  const [showExpertDropdown, setShowExpertDropdown] = useState(false);
  const [showPersonDropdown, setShowPersonDropdown] = useState(false);
  const [processedFailedTaskId, setProcessedFailedTaskId] = useState<string | null>(null);

  useEffect(() => {
    setIsMobileDevice(detectIsMobileDevice());
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showGenderDropdown && !target.closest('.gender-dropdown')) {
        setShowGenderDropdown(false);
      }
      if (showMaritalStatusDropdown && !target.closest('.marital-status-dropdown')) {
        setShowMaritalStatusDropdown(false);
      }
      if (showExpertDropdown && !target.closest('.expert-dropdown')) {
        setShowExpertDropdown(false);
      }
      if (showPersonDropdown && !target.closest('.person-dropdown')) {
        setShowPersonDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGenderDropdown, showMaritalStatusDropdown, showExpertDropdown, showPersonDropdown]);

  useEffect(() => {
    if (
      backgroundAnalysisStatus?.status === 'failed' &&
      backgroundAnalysisStatus?.data?.type === 'validation_error' &&
      backgroundAnalysisStatus?.data?.examples &&
      backgroundAnalysisStatus.taskId !== processedFailedTaskId
    ) {
      const suggestedQuestions = {
        questions: backgroundAnalysisStatus.data.examples,
        originalRequest: description,
        expert: selectedExpert,
        person: selectedPerson,
        customExpert: selectedExpert === 'custom' ? customExpert : undefined,
        customPerson: selectedPerson === 'custom' ? customPerson : undefined
      };

      saveSuggestedQuestions(suggestedQuestions);

      const finalExpert = selectedExpert === 'custom' ? customExpert : selectedExpert;
      const finalPerson = selectedPerson === 'custom' ? customPerson : selectedPerson;

      validateAndSendExpertStat(
        selectedExpert,
        selectedPerson,
        customExpert,
        customPerson,
        i18n.language
      ).finally(() => {
        if (description.trim()) {
          onStartAnalysis(description.trim(), finalExpert || undefined, finalPerson || undefined, undefined, true);
        }
      });

      setProcessedFailedTaskId(backgroundAnalysisStatus.taskId);
    }
  }, [
    backgroundAnalysisStatus,
    processedFailedTaskId,
    description,
    selectedExpert,
    selectedPerson,
    customExpert,
    customPerson,
    onStartAnalysis
  ]);

  const inputModeIndicator = useMemo(() => {
    if (isMobileDevice) {
      return { Icon: Smartphone, hint: t('formHintMobile') };
    }
    return { Icon: MonitorSmartphone, hint: t('formHintDesktop') };
  }, [isMobileDevice, t]);

  const validateExpert = useCallback((value: string): boolean => {
    const readableCharPattern = /^[a-zA-Zа-яА-ЯёЁ0-9\s\-.,()]*$/;
    return readableCharPattern.test(value);
  }, []);

  const sanitizeExpert = useCallback((value: string): string => {
    return value.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\s\-.,()]/g, '');
  }, []);

  const currentLength = description.length;
  const trimmedLength = description.trim().length;
  const isDescriptionValid = trimmedLength >= MIN_DESCRIPTION_LENGTH && currentLength <= MAX_DESCRIPTION_LENGTH;

  const handleExpertChange = useCallback((value: string) => {
    setSelectedExpert(value);
    setShowCustomExpert(value === 'custom');
    if (value !== 'custom') {
      setCustomExpert('');
    }
    if (value && value.trim()) {
      setSelectedPerson('');
      setShowCustomPerson(false);
      setCustomPerson('');
    }
  }, []);

  const handlePersonChange = useCallback((value: string) => {
    setSelectedPerson(value);
    setShowCustomPerson(value === 'custom');
    if (value !== 'custom') {
      setCustomPerson('');
    }
    if (value && value.trim()) {
      setSelectedExpert('');
      setShowCustomExpert(false);
      setCustomExpert('');
    }
  }, []);

  const handleExampleClick = useCallback(() => {
    const randomExample = getRandomExample(t, description);
    setDescription(randomExample);
  }, [description, t]);

  const handleDescriptionChange = useCallback((value: string) => {
    setDescription(value);

    if (highlightField) {
      setHighlightField(false);
    }

    if (backgroundAnalysisStatus && !isTyping) {
      setIsTyping(true);
      onCancel?.();
    }
  }, [backgroundAnalysisStatus, highlightField, isTyping, onCancel]);

  const handleQuickAnalysis = useCallback(async () => {
    if (!description.trim() || !isDescriptionValid) {
      setHighlightField(true);
      const textarea = document.getElementById('quick-description');
      textarea?.focus();
      setTimeout(() => {
        setHighlightField(false);
      }, 2000);
      return;
    }

    setIsAnalyzing(true);

    try {
      await validateAndSendExpertStat(
        selectedExpert,
        selectedPerson,
        customExpert,
        customPerson,
        i18n.language
      );

      let finalExpert = selectedExpert;
      if (selectedExpert === 'custom' && customExpert.trim()) {
        finalExpert = customExpert.trim();
      }

      let finalPerson = selectedPerson;
      if (selectedPerson === 'custom' && customPerson.trim()) {
        finalPerson = customPerson.trim();
      }

      const baseDescription = description.trim();
      const personalInfo: string[] = [];

      if (gender) {
        const genderText = gender === 'male' ? t('formGenderMale') :
                         gender === 'female' ? t('formGenderFemale') :
                         gender;
        personalInfo.push(`${t('formGenderLabel')}: ${genderText}`);
      }
      if (age) {
        personalInfo.push(`${t('formAgeLabel')}: ${t('formAgeYears', { age })}`);
      }
      if (maritalStatus) {
        const maritalStatusText = maritalStatus === 'married' ? t('formMaritalStatusMarried') :
                                 maritalStatus === 'single' ? t('formMaritalStatusSingle') :
                                 maritalStatus;
        personalInfo.push(`${t('formMaritalStatusLabel')}: ${maritalStatusText}`);
      }

      const finalDescription = personalInfo.length > 0
        ? `${baseDescription}\n\n${personalInfo.join('. ')}.`
        : baseDescription;

      onStartAnalysis(finalDescription, finalExpert || undefined, finalPerson || undefined);

      setLastSubmittedDescription(baseDescription);
      setIsTyping(false);
    } catch (validationError) {
      console.error('❌ QuickAnalysisForm - validation error:', validationError);

      let finalExpert = selectedExpert;
      if (selectedExpert === 'custom' && customExpert.trim()) {
        finalExpert = customExpert.trim();
      }

      let finalPerson = selectedPerson;
      if (selectedPerson === 'custom' && customPerson.trim()) {
        finalPerson = customPerson.trim();
      }

      const baseDescription = description.trim();
      const personalInfo: string[] = [];

      if (gender) {
        const genderText = gender === 'male' ? t('formGenderMale') :
                         gender === 'female' ? t('formGenderFemale') :
                         gender;
        personalInfo.push(`${t('formGenderLabel')}: ${genderText}`);
      }
      if (age) {
        personalInfo.push(`${t('formAgeLabel')}: ${t('formAgeYears', { age })}`);
      }
      if (maritalStatus) {
        const maritalStatusText = maritalStatus === 'married' ? t('formMaritalStatusMarried') :
                                 maritalStatus === 'single' ? t('formMaritalStatusSingle') :
                                 maritalStatus;
        personalInfo.push(`${t('formMaritalStatusLabel')}: ${maritalStatusText}`);
      }

      const finalDescription = personalInfo.length > 0
        ? `${baseDescription}\n\n${personalInfo.join('. ')}.`
        : baseDescription;

      onStartAnalysis(finalDescription, finalExpert || undefined, finalPerson || undefined);

      setLastSubmittedDescription(baseDescription);
      setIsTyping(false);
    } finally {
      setIsAnalyzing(false);
    }
  }, [
    description,
    isDescriptionValid,
    selectedExpert,
    selectedPerson,
    customExpert,
    customPerson,
    gender,
    age,
    maritalStatus,
    onStartAnalysis
  ]);

  const handleDescriptionKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isMobileDevice || event.key !== 'Enter') {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      const target = event.currentTarget;
      const { selectionStart, selectionEnd, value } = target;
      const updatedValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;
      setDescription(updatedValue);
      requestAnimationFrame(() => {
        const cursorPosition = selectionStart + 1;
        target.setSelectionRange(cursorPosition, cursorPosition);
      });
      return;
    }

    event.preventDefault();
    void handleQuickAnalysis();
  }, [handleQuickAnalysis, isMobileDevice]);

  return (
    <div className={cn('bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border border-white/20 relative z-20', className)}>
      <div className="mb-4">
        <label htmlFor="quick-description" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
          <span className="inline-flex items-center gap-1.5">
            {t('formDescribeSituation')}
            <span className="relative group inline-flex items-center">
              <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
              <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-48 sm:w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50 pointer-events-none whitespace-normal">
                {t('formDescribeHint')}
                <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></span>
              </span>
            </span>
          </span>
        </label>
        <div className="relative">
          <textarea
            id="quick-description"
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            onKeyDown={handleDescriptionKeyDown}
            onFocus={() => setHasDescriptionFocus(true)}
            onBlur={() => setHasDescriptionFocus(false)}
            placeholder={t('formPlaceholder')}
            className={`w-full px-3 sm:px-3 sm:px-4 py-1 sm:py-2 sm:py-3 pb-6 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none relative z-20 text-sm sm:text-base transition-all duration-300 ${
              highlightField || currentLength > MAX_DESCRIPTION_LENGTH
                ? 'border-red-400 ring-2 ring-red-300 bg-red-50'
                : trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION_LENGTH
                ? 'border-yellow-400 ring-2 ring-yellow-300 bg-yellow-50'
                : 'border-gray-300'
            }`}
            rows={3}
            maxLength={MAX_DESCRIPTION_LENGTH}
          />
          {!hasDescriptionFocus && inputModeIndicator && (
            <div className="absolute top-2 right-2 z-30 text-gray-500">
              <div
                className="group relative flex items-center cursor-default"
                aria-label={inputModeIndicator.hint}
                role="img"
              >
                <inputModeIndicator.Icon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                <div className="pointer-events-none absolute top-full right-0 mt-1 hidden group-hover:flex">
                  <span className="text-[10px] sm:text-xs bg-white border border-gray-200 rounded px-2 py-0.5 shadow-sm whitespace-nowrap">
                    {inputModeIndicator.hint}
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5 z-30">
            <div className={`text-xs opacity-60 pointer-events-none ${
              currentLength > MAX_DESCRIPTION_LENGTH ? 'text-red-600'
              : trimmedLength > 0 && trimmedLength < MIN_DESCRIPTION_LENGTH ? 'text-yellow-600'
              : 'text-gray-400'
            }`}>
              {description.length}/{MAX_DESCRIPTION_LENGTH || 300}
            </div>
            <button
              type="button"
              onClick={handleExampleClick}
              disabled={isAnalyzing}
              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto rounded hover:bg-gray-100"
              title={t('formExamplesTitle')}
            >
              <Wand2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <button
          type="button"
          onClick={() => setShowExpertSection(!showExpertSection)}
          className="w-full flex items-center justify-between text-left text-xs sm:text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-3 border border-gray-300 hover:border-gray-400 transition-all duration-200 bg-white hover:bg-gray-50"
        >
          <div className="flex items-center">
            <User className="h-4 w-4 mr-2" />
            <span>{t('formOptions')}</span>
          </div>
          {showExpertSection ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {showExpertSection && (
          <div className="mt-3 space-y-4">
            {showCustomPerson && (
              <div>
                <label htmlFor="customPerson" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t('formSpecifyPerson')}
                </label>
                <input
                  id="customPerson"
                  type="text"
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                    customPerson && !validateExpert(customPerson) ? 'border-red-300' : ''
                  }`}
                  placeholder={t('formSpecifyPersonPlaceholder')}
                  value={customPerson}
                  onChange={(e) => setCustomPerson(sanitizeExpert(e.target.value))}
                  maxLength={100}
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500">
                    Укажите известную личность, чьей позиции придерживаться при анализе.
                  </p>
                  <span className={`text-xs ${customPerson && !validateExpert(customPerson) ? 'text-red-600' : 'text-gray-500'}`}>
                    {customPerson && !validateExpert(customPerson) ? 'Недопустимые символы удалены' : `${customPerson.length}/100 символов`}
                  </span>
                </div>
                {customPerson && !validateExpert(customPerson) && (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠️ {t('formAllowedChars')}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="person-dropdown">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t('formChoosePerson')}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowPersonDropdown(!showPersonDropdown)}
                    disabled={listsLoading}
                    className="w-full px-4 py-3 text-sm text-left bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-all duration-200 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className={selectedPerson === '' ? 'text-gray-500' : 'text-gray-900'}>
                      {listsLoading ? t('formLoadingPersons') : selectedPerson === '' ? t('formChoosePersonPlaceholder') : persons.find(p => p.value === selectedPerson)?.label || selectedPerson}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showPersonDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showPersonDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePersonChange('');
                          setShowPersonDropdown(false);
                        }}
                        className="w-full px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors duration-200 first:rounded-t-lg"
                      >
                        {t('formChoosePersonPlaceholder')}
                      </button>
                      {persons.map((person) => (
                        <button
                          key={person.value}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePersonChange(person.value);
                            setShowPersonDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors duration-200 last:rounded-b-lg"
                        >
                          {person.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('formChoosePersonHint')}
                </p>
              </div>

              <div className="expert-dropdown">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t('formOrChooseExpert')}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowExpertDropdown(!showExpertDropdown)}
                    disabled={listsLoading}
                    className="w-full px-4 py-3 text-sm text-left bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-all duration-200 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className={selectedExpert === '' ? 'text-gray-500' : 'text-gray-900'}>
                      {listsLoading ? t('formLoadingExperts') : selectedExpert === '' ? t('formChooseExpertPlaceholder') : experts.find(e => e.value === selectedExpert)?.label || selectedExpert}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showExpertDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showExpertDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExpertChange('');
                          setShowExpertDropdown(false);
                        }}
                        className="w-full px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors duration-200 first:rounded-t-lg"
                      >
                        {t('formChooseExpertPlaceholder')}
                      </button>
                      {experts.map((expert) => (
                        <button
                          key={expert.value}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpertChange(expert.value);
                            setShowExpertDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors duration-200 last:rounded-b-lg"
                        >
                          {expert.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('formChooseExpertHint')}
                </p>

                {showCustomExpert && (
                  <div className="mt-3">
                    <label htmlFor="customExpert" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {t('formSpecifyExpert')}
                    </label>
                    <input
                      id="customExpert"
                      type="text"
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                        customExpert && !validateExpert(customExpert) ? 'border-red-300' : ''
                      }`}
                      placeholder={t('formSpecifyExpertPlaceholder')}
                      value={customExpert}
                      onChange={(e) => setCustomExpert(sanitizeExpert(e.target.value))}
                      maxLength={100}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-500">
                        {t('formSpecifyExpertHint')}
                      </p>
                      <span className={`text-xs ${customExpert && !validateExpert(customExpert) ? 'text-red-600' : 'text-gray-500'}`}>
                        {customExpert && !validateExpert(customExpert) ? t('formCharsRemoved') : t('formCharsCount', { count: customExpert.length })}
                      </span>
                    </div>
                    {customExpert && !validateExpert(customExpert) && (
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ {t('formAllowedChars')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative gender-dropdown">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t('formGender')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowGenderDropdown(!showGenderDropdown)}
                  className="w-full px-3 py-2 text-sm text-left bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-all duration-200 flex items-center justify-between"
                >
                  <span className={gender === '' ? 'text-gray-500' : 'text-gray-900'}>
                    {gender === '' ? t('formGenderNotSpecified') : gender === 'male' ? t('formGenderMale') : t('formGenderFemale')}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showGenderDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showGenderDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGender('');
                        setShowGenderDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors duration-200 first:rounded-t-lg"
                    >
                      {t('formGenderNotSpecified')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGender('male');
                        setShowGenderDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors duration-200"
                    >
                      {t('formGenderMale')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGender('female');
                        setShowGenderDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors duration-200 last:rounded-b-lg"
                    >
                      {t('formGenderFemale')}
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="age" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t('formAge')}
                </label>
                <input
                  id="age"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder={t('formAgePlaceholder')}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                />
              </div>

              <div className="relative marital-status-dropdown">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {t('formMaritalStatus')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowMaritalStatusDropdown(!showMaritalStatusDropdown)}
                  className="w-full px-3 py-2 text-sm text-left bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-all duration-200 flex items-center justify-between"
                >
                  <span className={maritalStatus === '' ? 'text-gray-500' : 'text-gray-900'}>
                    {maritalStatus === '' ? t('formMaritalStatusNotSpecified') :
                     maritalStatus === 'married' ? t('formMaritalStatusMarried') :
                     maritalStatus === 'single' ? t('formMaritalStatusSingle') : maritalStatus}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showMaritalStatusDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showMaritalStatusDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMaritalStatus('');
                        setShowMaritalStatusDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                    >
                      {t('formMaritalStatusNotSpecified')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMaritalStatus('married');
                        setShowMaritalStatusDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                    >
                      {t('formMaritalStatusMarried')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMaritalStatus('single');
                        setShowMaritalStatusDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                    >
                      {t('formMaritalStatusSingle')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-red-800">{t('formAnalysisError')}</h3>
              <p className="text-sm text-red-700 mt-1">
                {error}
              </p>
            </div>
            {onClearError && (
              <button
                onClick={onClearError}
                className="ml-3 flex-shrink-0 p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors -mt-1"
                title={t('formClose')}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <button
          onClick={handleQuickAnalysis}
            disabled={isLoading || !isDescriptionValid || (description.trim() === lastSubmittedDescription && lastSubmittedDescription !== '' && !error)}
          className={`w-full disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg py-3 sm:py-4 px-6 sm:px-8 relative z-10 overflow-hidden ${
            backgroundAnalysisStatus && backgroundAnalysisStatus.status !== 'completed' && backgroundAnalysisStatus.status !== 'failed'
              ? 'btn bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 active:bg-primary-800'
              : 'btn-primary'
          }`}
        >
          {/* Убрали прогресс-бар с кнопки - теперь используется полноэкранный прелоадер */}

          <div className="relative z-10 flex items-center justify-center">
            {backgroundAnalysisStatus ? (
              <>
                <div className="bg-white rounded-full mr-2">
                  <img
                    src="/images/preloader-plus-minus.svg"
                    alt={t('formLoadingAlt')}
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div>
                  <div className="font-medium text-center">
                    {backgroundAnalysisStatus.status === 'pending' ? t('formQueueWaiting') : t('formAnalysisRunning')}
                    {backgroundAnalysisStatus.estimatedTimeRemaining !== null && backgroundAnalysisStatus.estimatedTimeRemaining !== undefined && backgroundAnalysisStatus.estimatedTimeRemaining > 0 && (
                      <span className="text-xs opacity-90 ml-2">
                        • {t('formTimeRemaining')}: {formatTime(backgroundAnalysisStatus.estimatedTimeRemaining)}
                      </span>
                    )}
                  </div>
                </div>
              </>
            ) : isLoading ? (
              <>
                <div className="bg-white rounded-full mr-2">
                  <img
                    src="/images/preloader-plus-minus.svg"
                    alt={t('formLoadingAlt')}
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {t('formSendingToQueue')}
              </>
            ) : (
              <>
                <Brain className="w-5 h-5 mr-2" />
                {error && description.trim() === lastSubmittedDescription ? t('formRetryAnalysis') : (description.trim() === lastSubmittedDescription && lastSubmittedDescription !== '' ? t('formRequestUnchanged') : t('formGetAISolution'))}
              </>
            )}
          </div>
        </button>
      </div>
      <div className="mt-3 text-xs text-gray-500 text-center">
        {t('formTryFree')}
      </div>
    </div>
  );
};

export default memo(QuickAnalysisForm);

